import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  EEmailABTestWinningMetric,
  IEmailABTestSettings,
  TEmailABTestVariation,
} from '@feathr/blackbox';
import {
  EMAIL_AB_TEST_DURATION_HOURS_OPTIONS,
  EMAIL_AB_TEST_PERCENTAGE_OPTIONS,
} from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import { CardV2 as Card, Fieldset, Slider, Toggle, UniversalSelect } from '@feathr/components';
import {
  getVariationOptions,
  getWinningMetricHelpText,
  getWinningMetricOptions,
} from '@feathr/extender/utils/emailABTest';

export interface IMailrailABTestCardProps {
  disabled: boolean;
  onChange: (value: IEmailABTestSettings) => void;
  value: IEmailABTestSettings;
}

function MailrailABTestCard({
  disabled,
  onChange,
  value,
}: Readonly<IMailrailABTestCardProps>): JSX.Element {
  const { t } = useTranslation();

  const winningMetricOptions = getWinningMetricOptions(t);
  const variationOptions = getVariationOptions(t);
  const selectedWinningMetric = winningMetricOptions.find(
    (option) => option.id === value.winning_metric,
  );
  const selectedVariation = variationOptions.find(
    (option) => option.id === value.fallback_variation,
  );

  // Marks use the backend values and restrictToMarks, so the slider value is the ab_test value and keyboard/drag snap to the allowed options.
  const percentageMarks = EMAIL_AB_TEST_PERCENTAGE_OPTIONS.map((percentage) => ({
    label: t('{{percentage}}%', { percentage }),
    value: percentage,
  }));
  const durationMarks = EMAIL_AB_TEST_DURATION_HOURS_OPTIONS.map((hours) => ({
    label: t('{{hours}}h', { hours }),
    value: hours,
  }));
  const minPercentage = EMAIL_AB_TEST_PERCENTAGE_OPTIONS[0];
  const maxPercentage =
    EMAIL_AB_TEST_PERCENTAGE_OPTIONS[EMAIL_AB_TEST_PERCENTAGE_OPTIONS.length - 1];
  const minDuration = EMAIL_AB_TEST_DURATION_HOURS_OPTIONS[0];
  const maxDuration =
    EMAIL_AB_TEST_DURATION_HOURS_OPTIONS[EMAIL_AB_TEST_DURATION_HOURS_OPTIONS.length - 1];

  function handlePatch(patch: Partial<IEmailABTestSettings>): void {
    onChange({ ...value, ...patch });
  }

  function handleToggle(enabled?: boolean): void {
    handlePatch({ is_enabled: !!enabled });
  }

  function handleSelectWinningMetric(
    option: IUniversalSelectOption<EEmailABTestWinningMetric>,
  ): void {
    handlePatch({ winning_metric: option.id });
  }

  function handleSelectVariation(option: IUniversalSelectOption<TEmailABTestVariation>): void {
    handlePatch({ fallback_variation: option.id });
  }

  function handleChangePercentage(percentage: number): void {
    handlePatch({ test_percentage: percentage });
  }

  function handleChangeDuration(hours: number): void {
    handlePatch({ test_duration_hours: hours });
  }

  return (
    <Card>
      <Card.Header
        description={t(
          'Test two email versions and automatically send the winning version to the remaining audience.',
        )}
        title={t('A/B Test')}
      >
        <Toggle
          aria-label={t('A/B Test')}
          disabled={disabled}
          onChange={handleToggle}
          value={value.is_enabled}
        />
      </Card.Header>
      {value.is_enabled && (
        <Card.Content>
          <Fieldset>
            <UniversalSelect<IUniversalSelectOption<EEmailABTestWinningMetric>>
              disabled={disabled}
              helpText={getWinningMetricHelpText(value.winning_metric, t)}
              label={t('Winning metric')}
              name={'ab_test_winning_metric'}
              onSelectSingle={handleSelectWinningMetric}
              options={winningMetricOptions}
              value={selectedWinningMetric}
            />
            <UniversalSelect<IUniversalSelectOption<TEmailABTestVariation>>
              disabled={disabled}
              helpText={t('Used when results are tied')}
              label={t('Fallback winner')}
              name={'ab_test_fallback_variation'}
              onSelectSingle={handleSelectVariation}
              options={variationOptions}
              value={selectedVariation}
            />
            <Slider
              disabled={disabled}
              helpText={t(
                '{{percentage}}% of your audience will receive the test (split evenly between A and B). The remaining {{remaining}}% will receive the winning version.',
                { percentage: value.test_percentage, remaining: 100 - value.test_percentage },
              )}
              label={t('Test audience percentage')}
              marks={percentageMarks}
              max={maxPercentage}
              min={minPercentage}
              name={'ab_test_test_percentage'}
              onChange={handleChangePercentage}
              restrictToMarks={true}
              thumbLabel={t('Test audience percentage')}
              tickLabel={null}
              value={value.test_percentage}
            />
            <Slider
              disabled={disabled}
              helpText={t(
                'After {{hours}} hour(s), the system will determine a winner and send to the remaining audience.',
                { hours: value.test_duration_hours },
              )}
              label={t('Test duration')}
              marks={durationMarks}
              max={maxDuration}
              min={minDuration}
              name={'ab_test_test_duration_hours'}
              onChange={handleChangeDuration}
              restrictToMarks={true}
              thumbLabel={t('Test duration')}
              tickLabel={null}
              value={value.test_duration_hours}
            />
          </Fieldset>
        </Card.Content>
      )}
    </Card>
  );
}

export default observer(MailrailABTestCard);
